import IService from '../IService';

export default IService.extend({
  /**
   * POST new url uploaded asset, receive in return assetUUID
   *
   * @param postData
   * @return {*|ColdPromise}
   * with response (array[...assetMetadata])
   *
   */
  initUrlUpload(assetData) {
    const postData = _.options({
      completeUploadRequestModels: [assetData],
    });

    const rv = new _.ColdPromise();

    $.ajax({
      url: app.apiUrl('/api/v2/assets/upload_url'),
      type: 'POST',

      contentType: 'application/json',
      data: JSON.stringify(postData),

      error: (jqXHR, textStatus) => {
        if (textStatus !== 'abort') {
          rv.reject(this.createError(jqXHR));
        }
      },

      dataType: 'json',
      success(res) {
        if (_.isArray(res) && _.isPlainObject(res[0]) && res[0].assetUUID) {
          rv.resolve(res[0]);
        } else {
          rv.reject({ error: 'UNSUPPORTED_RESPONSE_FORMAT' });
        }
      },
    });

    return rv;
  },

  /**
   * POST new asset (V3 multipart), receive presigned URLs for each part
   *
   * @param data
   * @return {*|ColdPromise}
   * with response:
   *  assetUUID (string)
   *  uploadId (string)
   *  bucketName (string)
   *  s3Path (string)
   *  partPresignedUrls (array[{partNumber, url}])
   */
  initFileUploadV3(data) {
    data = _.options(data, {
      mimeType: null,
      fileName: null,
      uploadType: null,
      numberOfParts: 1,
    });
    const rv = new _.ColdPromise();

    $.ajax({
      url: app.apiUrl('/api/v3/assets'),
      type: 'POST',

      contentType: 'application/json',
      data: JSON.stringify(data),

      error: (jqXHR, textStatus) => {
        if (textStatus !== 'abort') {
          rv.reject(this.createError(jqXHR));
        }
      },

      dataType: 'json',
      success(res) {
        if (
          _.isPlainObject(res) &&
          res.assetUUID &&
          res.uploadId &&
          res.bucketName &&
          res.s3Path &&
          _.isArray(res.partPresignedUrls)
        ) {
          res.mimeType = data.mimeType;
          rv.resolve(res);
        } else {
          rv.reject({ error: 'UNSUPPORTED_RESPONSE_FORMAT' });
        }
      },
    });

    return rv;
  },

  /**
   * Abort a V3 multipart upload, cleaning up uploaded parts from S3
   *
   * @param assetUUID {string}
   * @param uploadId {string}
   * @param fileName {string}
   * @return {*|ColdPromise}
   */
  abortMultipartUpload(assetUUID, uploadId, fileName) {
    const rv = new _.ColdPromise();

    $.ajax({
      url: app.apiUrl(
        `/api/v3/assets/${assetUUID}/upload/${uploadId}?fileName=${encodeURIComponent(
          fileName
        )}`
      ),
      type: 'DELETE',

      error: (jqXHR, textStatus) => {
        if (textStatus !== 'abort') {
          rv.reject(this.createError(jqXHR));
        }
      },

      success: () => rv.resolve(),
    });

    return rv;
  },

  /**
   * POST new asset, receive in return assetUUID
   *
   * @param data
   * @return {*|ColdPromise}
   * with response:
   *  assetUUID
   *  policy (base64)
   *  signature (base64)
   *  bucketName (string)
   *  s3Path (string) // aka folder name
   */
  initFileUpload(data) {
    data = _.options(data, {
      mimeType: null,
      fileName: null,
      uploadType: null,
    });
    const rv = new _.ColdPromise();

    $.ajax({
      url: app.apiUrl('/api/v2/assets'),
      type: 'POST',

      contentType: 'application/json',
      data: JSON.stringify(data),

      error: (jqXHR, textStatus) => {
        if (textStatus !== 'abort') {
          rv.reject(this.createError(jqXHR));
        }
      },

      dataType: 'json',
      success(res) {
        if (
          _.isPlainObject(res) &&
          res.assetUUID &&
          res.policy &&
          res.signature &&
          res.bucketName &&
          res.s3Path
        ) {
          // pass as part of `uploadConfig` for simplicity
          res.mimeType = data.mimeType;
          rv.resolve(res);
        } else {
          rv.reject({ error: 'UNSUPPORTED_RESPONSE_FORMAT' });
        }
      },
    });

    return rv;
  },

  /**
   * PUT new edits asset
   * @param data
   * @param {{assetUUID, mediaAssetType}}
   * @return {*|ColdPromise}
   */
  editAsset(data, { assetUUID = null, mediaAssetType = null }) {
    const rv = new _.ColdPromise();

    if (mediaAssetType === 'VIDEO_SEGMENT') {
      // updating asset data of selected fragments should represent original video asset
      mediaAssetType = 'VIDEO';
    }

    const payload = Object.assign(
      {
        mediaAssetType,
      },
      data
    );

    $.ajax({
      url: app.apiUrl('/api/v1/assets/' + assetUUID),
      type: 'PUT',

      contentType: 'application/json',
      data: JSON.stringify(payload),

      error: (jqXHR, textStatus, errorThrown) => {
        if (textStatus !== 'abort') {
          rv.reject(this.createError(jqXHR));
        }
      },

      success: function (res) {
        rv.resolve(res);
      },
    });

    return rv;
  },

  // ensure uniqueness of xhr requests by assetUUID
  activeFetches: new Map(),
  fetch(assetUUID, isNarration, storyId) {
    const rv = new _.ColdPromise();

    $.ajax({
      url: isNarration
        ? app.apiUrl(`/api/v1/draftStories/${storyId}/narrations/${assetUUID}`)
        : app.apiUrl(`/api/v1/assets/${assetUUID}`),
      type: 'GET',

      error: (jqXHR, textStatus) => {
        if (textStatus !== 'abort') {
          rv.reject({ error: textStatus });
        }
      },

      success: (response) => {
        if (isNarration) {
          if (_.isPlainObject(response)) {
            app.prependHttpAcrossObject(response.asset);
            response.cdnUrl = app.prependHttp(response.cdnUrl);
            response.url = app.prependHttp(response.url);

            rv.resolve(response);
          } else {
            rv.reject({ error: 'BAD_FORMAT' });
          }
        } else {
          if (
            _.isPlainObject(response) &&
            'OK' === response.response &&
            _.isArray(response.assets) &&
            !_.isEmpty(response.assets)
          ) {
            const asset = response.assets[0];
            app.prependHttpAcrossObject(asset);
            rv.resolve(asset);
          } else {
            rv.reject({ error: 'BAD_FORMAT' });
          }
        }
      },
    });

    return rv;
  },

  /**
   * Adapt external assets
   * Example: you have video from instagram, wrapped into Wochit asset model,
   * you send it to backend so it will ingest it to rest of the platform
   */
  convert(asset) {
    asset = _.options(asset, {
      id: app.guid(),
    });

    return new Promise((resolve, reject) => {
      $.ajax({
        url: app.apiUrl(`/api/v1/assets/${asset.metadata.assetUUID}`),
        type: 'POST',
        contentType: 'application/json',
        data: JSON.stringify({ asset }),
        error: (jqXHR, textStatus) => {
          if (textStatus !== 'abort') {
            reject(this.createError(jqXHR));
          }
        },
        dataType: 'json',
        success: (res) => {
          if (res.id) {
            resolve(res);
          } else {
            reject({ error: 'BAD_RESPONSE' });
          }
        },
      });
    });
  },

  postYoutube(data) {
    const rv = new _.ColdPromise();

    $.ajax({
      url: app.apiUrl('/api/v1/assets/youtube', {
        url: data.url,
      }),
      type: 'POST',

      contentType: 'application/json;charset=UTF-8',
      data: JSON.stringify(data),

      error: (jqXHR, textStatus) => {
        if (textStatus !== 'abort') {
          rv.reject(this.createError(jqXHR));
        }
      },

      dataType: 'json',
      success: (res) => {
        if (res.response === 'OK' && !_.isEmpty(res.assets)) {
          rv.resolve(res.assets[0]);
        } else if (
          'FAILED_GETTING_YOUTUBE_VIDEO_UPON_YOUTUBE_RESTRICTIONS' ===
          res.response
        ) {
          rv.reject({ error: app.culture.get('uploadView.error.restricted') });
        } else if ('FAILED_SAVING_FILE' === res.response) {
          rv.reject({ error: app.culture.get('uploadView.error.saving') });
        } else {
          rv.reject({
            error: `${app.culture.get('uploadView.error.unknown')}`,
          });
        }
      },
    });

    return rv;
  },

  delete(assetUUID) {
    const rv = new _.ColdPromise();

    $.ajax({
      url: app.apiUrl('/api/v1/assets/' + assetUUID),
      async: true,
      method: 'DELETE',
      error: (jqXHR, textStatus, errorThrown) => {
        rv.reject(errorThrown);
      },
      success: () => rv.resolve(),
    });

    return rv;
  },

  favorite({ assetUUID, providerAssetId }, assignFavoriteAction = true) {
    const rv = new _.ColdPromise();
    $.ajax({
      url:
        app.apiUrl('/api/v1/assets/favorite?') +
        $.param({
          assetUUID: assetUUID,
        }),
      async: true,
      method: assignFavoriteAction ? 'POST' : 'DELETE',
      error: (jqXHR, textStatus, errorThrown) => {
        rv.reject(errorThrown);
      },
      success: () => rv.resolve(),
    });

    return rv;
  },
});
